import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useId, useMemo } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";
import { RefreshBar, RefreshLabel } from "@/shared/ui/refresh";

import { useAssets } from "../../api/useAssets";
import { useMatches } from "../../api/useMatches";
import { useMatchHistory } from "../../api/useMatchHistory";
import { useStartAnalysis } from "../../api/useStartAnalysis";
import { useStatsSummary } from "../../api/useStatsSummary";
import { addDays } from "../../lib/addDays";
import { formatPercent } from "../../lib/formatPercent";
import { hasFilters } from "../../lib/hasFilters";
import { heroStats } from "../../lib/heroStats";
import type { MatchFilters as Filters } from "../../lib/matchFiltersSchema";
import { FreshMatchesHint } from "../FreshMatchesHint";
import { MatchFilters } from "../MatchFilters";
import { HistoryLoading } from "./HistoryLoading";
import { MatchListEmpty } from "./MatchListEmpty";
import { MatchListError } from "./MatchListError";
import { MatchListSkeleton } from "./MatchListSkeleton";
import { MatchRow } from "./MatchRow";

// Сколько матчей на странице: столько отдаёт бэкенд по умолчанию.
const PAGE_SIZE = 20;

const MVP_SHARE = msg({
  id: "matches.places.mvp",
  message: "{known, plural, one {MVP in {mvp} of # match} other {MVP in {mvp} of # matches}}",
});
const TOP_SHARE = msg({
  id: "matches.places.top",
  message:
    "{known, plural, one {In the top three in {top} of # match} other {In the top three in {top} of # matches}}",
});

const SHOW_MORE = msg({
  message: "{count, plural, one {Show # more match} other {Show # more matches}}",
});

type MatchListProps = {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  // Метка другой фичи в строке матча рядом с местом в матче (хайлайты матча): её подставляет
  // маршрут, список про чужие фичи не знает.
  renderBadge?: (matchId: number) => ReactNode;
};

// История матчей игрока с фильтрами и запуск разбора (макет `design/prototype/Home.dc.html`).
// Фильтры живут в URL у страницы, фильтрует бэкенд — по всей истории. Страницами по 20,
// «Показать ещё» догружает следующую: так можно дойти до самого первого матча.
export const MatchList = ({ filters, onFiltersChange, renderBadge }: MatchListProps) => {
  const { i18n } = useLingui();
  const headingId = useId();
  const history = useMatchHistory(filters);
  const { query: matches, items, total, wins, places } = history;
  // Последние матчи без фильтров — тот же кэш, что у сводки: из них герои для выбора,
  // пока сводки игрока нет. По отфильтрованному списку нельзя: выбор героя сузил бы сам выбор.
  const recent = useMatches();
  const assets = useAssets();
  const stats = useStatsSummary();
  const navigate = useNavigate();
  const startAnalysis = useStartAnalysis({
    onStarted: (analysis) =>
      navigate({ to: "/matches/$matchId", params: { matchId: analysis.match_id } }),
  });

  const heroes = useMemo(
    () => new Map((assets.data?.heroes ?? []).map((hero) => [hero.id, hero])),
    [assets.data],
  );
  const filtered = hasFilters(filters);
  // Герои для выбора — за всю историю из сводки; пока её нет — по последним матчам.
  const allTimeHeroes = stats.data?.summary?.heroes ?? [];
  const played =
    allTimeHeroes.length > 0
      ? allTimeHeroes.map((hero) => ({
          heroId: hero.hero_id,
          games: hero.matches,
          wins: hero.wins,
        }))
      : heroStats(recent.items);
  const loadMore = Math.min(PAGE_SIZE, total - items.length);
  // Список перечитывается (фильтр, событие разбора), на экране пока прошлый: полоса и «обновляем».
  const refreshing = matches.isFetching && !matches.isPending && !matches.isFetchingNextPage;

  const renderBody = () => {
    if (matches.isPending) {
      return <MatchListSkeleton />;
    }
    if (matches.isError) {
      return (
        <MatchListError
          error={matches.error}
          isRetrying={matches.isFetching}
          onRetry={() => void matches.refetch()}
        />
      );
    }
    if (history.isWaitingForHistory && !filtered) {
      return <HistoryLoading />;
    }
    if (total === 0 && !filtered) {
      return (
        <MatchListEmpty
          isRefreshing={matches.isFetching}
          onRefresh={() => void matches.refetch()}
        />
      );
    }
    return (
      <>
        <MatchFilters
          filters={filters}
          onChange={onFiltersChange}
          played={played}
          matchDays={items.map((match) => addDays(new Date(match.started_at), 0))}
          now={new Date()}
        />
        {filters.place !== undefined && (
          <p className="text-[13px] text-muted">
            {i18n._({ ...(filters.place === "mvp" ? MVP_SHARE : TOP_SHARE), values: places })}
          </p>
        )}
        {items.length === 0 ? (
          <p className="rounded-[10px] border border-dashed border-border p-6 text-center text-muted">
            <Trans>No matches for these filters</Trans>
          </p>
        ) : (
          <ul
            aria-busy={matches.isPlaceholderData}
            className={cn(
              "flex flex-col gap-1.5 transition-opacity",
              matches.isPlaceholderData && "opacity-60",
            )}
          >
            {items.map((match) => (
              <MatchRow
                key={match.match_id}
                match={match}
                hero={heroes.get(match.hero_id)}
                isStarting={startAnalysis.pendingMatchId === match.match_id}
                startDisabled={startAnalysis.isPending}
                startError={
                  startAnalysis.failedMatchId === match.match_id ? startAnalysis.error : null
                }
                onStart={startAnalysis.start}
                badge={renderBadge?.(match.match_id)}
              />
            ))}
          </ul>
        )}
        {matches.hasNextPage && loadMore > 0 && (
          <Button
            variant="outline"
            className="self-center"
            loading={matches.isFetchingNextPage}
            onClick={() => void matches.fetchNextPage()}
          >
            {matches.isFetchingNextPage ? (
              <Trans>Loading…</Trans>
            ) : (
              i18n._({ ...SHOW_MORE, values: { count: loadMore } })
            )}
          </Button>
        )}
      </>
    );
  };

  const shownCount = items.length;
  const totalCount = total;
  const winRate = formatPercent(totalCount === 0 ? 0 : wins / totalCount, i18n.locale);

  return (
    <section
      aria-labelledby={headingId}
      aria-busy={refreshing || undefined}
      className="relative flex min-w-0 flex-col gap-2.5 rounded-xl border border-border bg-surface p-5"
    >
      <RefreshBar active={refreshing} />
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={headingId} className="font-display text-[26px]">
          <Trans>Recent matches</Trans>
        </h2>
        <RefreshLabel active={refreshing} className="ml-auto" />
        {matches.isSuccess && (totalCount > 0 || filtered) && (
          <span className="text-sm text-muted">
            <Trans>
              {shownCount} of {totalCount} · {winRate} wins
            </Trans>
          </span>
        )}
      </div>
      {renderBody()}
      {matches.isSuccess && totalCount > 0 && <FreshMatchesHint variant="footer" />}
    </section>
  );
};
